import clsx from 'clsx';
import React from 'react';

import { useInvertedColor } from '../InvertedColorContext';
import type { ScrollFaderController } from './useScrollFaderController';

const fadeColor = {
    default: {
        start: 'before:from-(--sp-color-bg-base)',
        end: 'after:from-(--sp-color-bg-base)',
    },
    sidebar: {
        start: 'before:from-(--sp-color-bg-side)',
        end: 'after:from-(--sp-color-bg-side)',
    },
    inverted: {
        start: 'before:from-(--neutral-solid)',
        end: 'after:from-(--neutral-solid)',
    },
} as const;

type ScrollFaderKind = keyof typeof fadeColor;

/**
 * Indicate whether a scrollable box can scroll with a fade out effect.
 */
export function ScrollFader(props: {
    className?: string;
    children: React.ReactNode;
    controller: ScrollFaderController;
    kind?: ScrollFaderKind;
}) {
    const { children, className, controller, kind = 'default' } = props;
    const { resizeRef, start, end, orientation } = controller;
    const inverted = useInvertedColor();
    const variant = inverted ? 'inverted' : kind;

    return (
        <div
            ref={resizeRef}
            aria-orientation={orientation}
            className={clsx(
                'relative h-full overscroll-contain',
                start &&
                    clsx(
                        "before:pointer-events-none before:absolute before:inset-0 before:z-[1] before:bg-gradient-to-r before:from-0% before:to-transparent before:to-10% before:content-[''] aria-[orientation='vertical']:before:bg-gradient-to-b",
                        fadeColor[variant].start
                    ),
                end &&
                    clsx(
                        "after:pointer-events-none after:absolute after:inset-0 after:z-[1] after:bg-gradient-to-l after:from-0% after:to-transparent after:to-10% after:content-[''] aria-[orientation='vertical']:after:bg-gradient-to-t",
                        fadeColor[variant].end
                    ),
                className
            )}
        >
            {React.Children.only(children)}
        </div>
    );
}
